import { refreshToken } from '@booking-portal/api/services/auth-service';
import { getHost } from '@booking-portal/composables/get-host';
import { useSentry } from '@booking-portal/composables/use-sentry';
import { defaultLanguage, i18n } from '@booking-portal/config/i18n-config';
import { queryClient } from '@booking-portal/plugins/vue-query-plugin';
import { router } from '@booking-portal/router';
import { navigationNames } from '@booking-portal/router/router-constants';
import { useAuthStore } from '@booking-portal/stores/auth-store';
import { getApiEndpoint } from '@booking-portal/utils/env-utils';
import {
  isAxiosErrorResponse,
  isMigrationBlockedError,
} from '@booking-portal/utils/error-utils';
import { notify } from '@kyvg/vue3-notification';
import { captureException } from '@sentry/vue';
import {
  type AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
  create,
  isAxiosError,
} from 'axios';
import memoize from 'memoize';
import { createTrustedOriginChecker, safeOrigin } from 'shared/utils';
import type { Ref } from 'vue';

// If VITE_APP_ENDPOINT is a relative path, the trusted origin is the page's own origin.
const apiOrigin = safeOrigin(getApiEndpoint()) ?? window.location.origin;
const trustedOrigins = new Set([apiOrigin]);

const isTrustedKinnovisRequest = createTrustedOriginChecker(trustedOrigins);

type IAuthRequestConfig<T> = InternalAxiosRequestConfig<T> & { sent?: boolean };

declare module 'axios' {
  interface AxiosRequestConfig {
    // Statuses the caller handles itself — don't report them to Sentry.
    ignoredErrorStatuses?: number[];
  }
}

/**
 * A fully configured httpClient that redirects the user to the login if they request a secured endpoint that returns unauthorized.
 */
export const httpClient = create({
  baseURL: getApiEndpoint(),
});

/**
 * A http client that does not force the user to login if a response returns unauthorized.
 * It just does not handle any errors.
 */
export const publicHttpClient = create({
  baseURL: getApiEndpoint(),
});

const maxAge = 5000;
export const memoizedRefreshToken = memoize(refreshToken, {
  maxAge,
});

function requestHandler<T>(config: InternalAxiosRequestConfig<T>) {
  if (!isTrustedKinnovisRequest(config)) {
    return config;
  }

  const { captureException } = useSentry();
  const authStore = useAuthStore();
  let locale = defaultLanguage;

  try {
    // Hack because types are wrong in i18n package
    locale = (i18n.global.locale as unknown as Ref<string>).value;
  } catch (e) {
    captureException(e);
  }

  // Append user language
  if (!config.headers?.['Content-Language']) {
    config.headers['Content-Language'] = locale;
  }

  // Append Origin. Used to determine the tenant
  config.headers['X-KIN-Origin'] = getHost() || '';

  // Append access token to each request if available
  if (authStore.accessToken) {
    config.headers['Authorization'] = `Bearer ${authStore.accessToken}`;
  }

  return config;
}

// Parallel requests all fail with the same 403, so notify once and refetch `/setup`
// so the banner and basket gate appear even if the block started mid-session.
const notifyMigrationBlocked = memoize(
  () => {
    notify({
      type: 'error',
      text: i18n.global.t('migrationBlock.notification'),
    });
    queryClient.refetchQueries({ queryKey: ['setupData'], type: 'active' });
  },
  { maxAge: 5000 }
);

function handleMigrationBlockedError(error: unknown): boolean {
  if (!isMigrationBlockedError(error)) {
    return false;
  }

  notifyMigrationBlocked();
  return true;
}

async function responseErrorHandler<T, D>(error: AxiosError<T, D>) {
  const authStore = useAuthStore();
  const config: IAuthRequestConfig<T> = error.config as IAuthRequestConfig<T>;

  const url: string = error.request.responseURL || '';

  // Skip error checks when the request was sent to the auth-api.
  // Requests against the auth API need to be treated differently.
  // All requests against the auth API are handled by a different axios instance though. But better be safe than sorry.
  if (url.endsWith('/oauth/token')) {
    throw error;
  }

  const refreshToken = authStore.refreshToken;

  // Check if we are dealing with a 401 and if we have not sent a refresh request yet.
  if (
    isAxiosError(error) &&
    error.response?.status === 401 &&
    !config?.sent &&
    refreshToken
  ) {
    // Mark the request config as sent to prevent infinite loops
    config.sent = true;

    // Set auth state to refreshing token to update the UI if necessary
    authStore.authState = 'refreshing-token';

    try {
      // Using memoized method here to prevent multiple refresh requests at once
      const response = await memoizedRefreshToken(refreshToken);
      // Successful refresh: Setting new auth data to store.
      authStore.setAuthResponse(response.data);

      // Retry the original request
      return httpClient(requestHandler(config));
    } catch {
      // Refresh failed: Logging out because refresh token is invalid
      await authStore.logout().then(() => {
        return router.push({ name: navigationNames.login });
      });
      throw error;
    }
  } else {
    if (handleMigrationBlockedError(error)) {
      throw error;
    }

    // Any other error will be handled here
    if (isAxiosErrorResponse(error)) {
      if (error.response?.status === 401) {
        // User tries to access secured API. Logging out (if user is logged in)
        await authStore.logout().then(() => {
          return router.push({ name: navigationNames.login });
        });
        throw error;
      } else {
        const status = error.response?.status;
        const ignoredStatuses = config?.ignoredErrorStatuses ?? [];

        // Report to Sentry unless the caller opted out of this status.
        if (status == null || !ignoredStatuses.includes(status)) {
          captureException(error);
        }
      }
    }

    throw error;
  }
}

async function publicResponseErrorHandler<T, D>(error: AxiosError<T, D>) {
  if (handleMigrationBlockedError(error)) {
    throw error;
  }

  captureException(error);
  throw error;
}

export async function responseSuccessHandler(response: AxiosResponse) {
  // Nothing to do yet on successful responses
  return response;
}

httpClient.interceptors.request.use(requestHandler);
httpClient.interceptors.response.use(
  responseSuccessHandler,
  responseErrorHandler
);

publicHttpClient.interceptors.request.use(requestHandler);
publicHttpClient.interceptors.response.use(
  responseSuccessHandler,
  publicResponseErrorHandler
);
